/**
 * Repositório Documental — evidências + fluxo de aprovação pelo Órgão de Gestão.
 * Route: /documents
 */
import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { FolderOpen, Plus, Upload, Send, CheckCircle2, XCircle, Trash2, Loader2, Search, Download, ExternalLink, ShieldCheck, Clock, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const STATUS_META = {
  draft: { label: 'Rascunho', tone: 'bg-slate-100 text-slate-700 border-slate-200' },
  pending_approval: { label: 'Pendente Aprovação', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  approved: { label: 'Aprovado', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected: { label: 'Rejeitado', tone: 'bg-red-50 text-red-700 border-red-200' },
  archived: { label: 'Arquivado', tone: 'bg-slate-100 text-slate-500 border-slate-200' },
};

const CATEGORY_LABELS = {
  policy: 'Política', procedure: 'Procedimento', plan: 'Plano', evidence: 'Evidência',
  certificate: 'Certificado', contract: 'Contrato', audit_report: 'Relatório Auditoria',
  incident_report: 'Relatório Incidente', risk_assessment: 'Avaliação de Risco',
  dpia_document: 'DPIA', training_material: 'Formação', other: 'Outro',
};

const MODULE_LABELS = {
  bcm: 'Continuidade (BCM)', dpo: 'DPO / RGPD', policy: 'Políticas', audit: 'Auditorias',
  requirement: 'Requisitos NIS2', incident: 'Incidentes', asset: 'Ativos', supplier: 'Fornecedores',
  training: 'Formação', risk: 'Riscos', board: 'Órgão de Gestão', general: 'Geral',
};

const emptyForm = {
  title: '', description: '', category: 'evidence', linked_module: 'general',
  linked_entity_id: '', requires_board_approval: false, tags: '', external_url: '',
  document_date: new Date().toISOString().slice(0, 10),
};

export default function Documents() {
  const { user } = useAuth();
  const canCreate = user?.role === 'admin' || user?.role === 'ciso' || user?.role === 'dpo';
  const isBoardMember = user?.role === 'board_member' || user?.role === 'admin';

  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [filters, setFilters] = useState({ status: '', category: '', linked_module: '', search: '' });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [fileB64, setFileB64] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileMime, setFileMime] = useState('');
  const [saving, setSaving] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => { document.title = 'Documentos · NIS2AI'; }, []);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
        const [s, l] = await Promise.all([
          api.get('/documents/stats'),
          api.get(`/documents?${params}&page_size=100`),
        ]);
        if (ignore) return;
        setStats(s.data);
        setItems(l.data.items || []);
      } catch (err) {
        if (!ignore) toast.error(err?.response?.data?.detail || 'Falha ao carregar.');
      } finally { if (!ignore) setLoading(false); }
    })();
    return () => { ignore = true; };
  }, [refresh, filters]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error('Ficheiro excede 10 MB. Utilize URL externo.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const base64 = result.split(',')[1] || result;
      setFileB64(base64);
      setFileName(f.name);
      setFileMime(f.type || 'application/octet-stream');
    };
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!form.title || !form.category) { toast.error('Título e categoria são obrigatórios.'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(',').map(s => s.trim()).filter(Boolean),
        content_base64: fileB64 || undefined,
        file_name: fileName || undefined,
        mime_type: fileMime || undefined,
        linked_entity_id: form.linked_entity_id || undefined,
        external_url: form.external_url || undefined,
      };
      await api.post('/documents', payload);
      toast.success('Documento criado em rascunho.');
      setShowForm(false);
      setForm(emptyForm);
      setFileB64(null); setFileName(''); setFileMime('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      setRefresh(r => r + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
    finally { setSaving(false); }
  };

  const submitForApproval = async (doc) => {
    try {
      await api.post(`/documents/${doc.doc_id}/submit`);
      toast.success('Submetido ao Órgão de Gestão.');
      setRefresh(r => r + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
  };

  const approve = async (doc) => {
    if (!window.confirm(`Confirma aprovação de "${doc.title}"? Esta ação fica assinada digitalmente.`)) return;
    try {
      await api.post(`/documents/${doc.doc_id}/approve`);
      toast.success('Documento aprovado e assinado.');
      setRefresh(r => r + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
  };

  const reject = async () => {
    if (!rejectReason.trim()) { toast.error('Motivo obrigatório.'); return; }
    try {
      await api.post(`/documents/${rejectDialog.doc_id}/reject`, { reason: rejectReason });
      toast.success('Documento rejeitado.');
      setRejectDialog(null);
      setRejectReason('');
      setRefresh(r => r + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Remover "${doc.title}"?`)) return;
    try {
      await api.delete(`/documents/${doc.doc_id}`);
      toast.success('Removido.');
      setRefresh(r => r + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
  };

  const download = async (doc) => {
    try {
      const { data } = await api.get(`/documents/${doc.doc_id}/download`);
      if (data.external_url) {
        window.open(data.external_url, '_blank', 'noopener');
        return;
      }
      if (data.content_base64) {
        const link = document.createElement('a');
        link.href = `data:${data.mime_type || 'application/octet-stream'};base64,${data.content_base64}`;
        link.download = data.file_name || `${doc.doc_id}.bin`;
        link.click();
      } else {
        toast.info('Documento sem ficheiro anexado (apenas metadados).');
      }
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha ao descarregar.'); }
  };

  return (
    <Layout title="Repositório Documental">
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="documents-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
              <FolderOpen className="h-6 w-6 text-indigo-600" /> Repositório Documental
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Evidências, políticas e documentos com fluxo de aprovação pelo <strong>Órgão de Gestão</strong> (NIS2 Art. 20). Assinatura SHA-256 na aprovação para não-repúdio.
            </p>
          </div>
          {canCreate && <Button onClick={() => setShowForm(true)} data-testid="doc-new-btn"><Plus className="h-3.5 w-3.5 mr-1.5" /> Novo documento</Button>}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { key: 'total', label: 'Total', value: stats?.total ?? 0, icon: FileText, tone: 'from-indigo-500 to-indigo-600' },
            { key: 'pending', label: 'Aguardam aprovação', value: stats?.board_approval_needed ?? 0, icon: Clock, tone: 'from-amber-500 to-amber-600' },
            { key: 'approved', label: 'Aprovados', value: stats?.approved ?? 0, icon: ShieldCheck, tone: 'from-emerald-500 to-emerald-600' },
            { key: 'rejected', label: 'Rejeitados', value: (stats?.by_status?.rejected ?? 0), icon: XCircle, tone: 'from-red-500 to-red-600' },
          ].map(k => (
            <Card key={k.key} data-testid={`doc-kpi-${k.key}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{k.label}</span>
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${k.tone} flex items-center justify-center`}><k.icon className="h-4 w-4 text-white" /></div>
                </div>
                <div className="text-3xl font-bold text-slate-900 tabular-nums">{k.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4 flex flex-wrap gap-3">
            <div className="flex-1 min-w-64 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Pesquisar por título ou descrição..." className="pl-10"
                value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })}
                data-testid="doc-search-input" />
            </div>
            <Select value={filters.status || 'all'} onValueChange={v => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
              <SelectTrigger className="w-44" data-testid="doc-filter-status"><SelectValue placeholder="Estado" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os estados</SelectItem>
                {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.category || 'all'} onValueChange={v => setFilters({ ...filters, category: v === 'all' ? '' : v })}>
              <SelectTrigger className="w-44" data-testid="doc-filter-category"><SelectValue placeholder="Categoria" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as categorias</SelectItem>
                {Object.entries(CATEGORY_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.linked_module || 'all'} onValueChange={v => setFilters({ ...filters, linked_module: v === 'all' ? '' : v })}>
              <SelectTrigger className="w-44" data-testid="doc-filter-module"><SelectValue placeholder="Módulo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os módulos</SelectItem>
                {Object.entries(MODULE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* List */}
        <Card>
          <CardContent className="p-0">
            {loading ? <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-indigo-600" /></div> :
              items.length === 0 ? <div className="p-8 text-center text-slate-400 text-sm">Sem documentos. Crie um em &quot;Novo documento&quot;.</div> :
              <div className="divide-y divide-slate-100">
                {items.map(d => {
                  const sm = STATUS_META[d.status] || {};
                  const isOwner = d.created_by === user?.email;
                  const canEdit = canCreate && (d.status === 'draft' || d.status === 'rejected');
                  const canSubmit = canCreate && (d.status === 'draft' || d.status === 'rejected') && d.requires_board_approval;
                  const canApprove = isBoardMember && d.status === 'pending_approval';
                  return (
                    <div key={d.doc_id} className="p-4 hover:bg-slate-50" data-testid={`doc-${d.doc_id}`}>
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                          <FileText className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h3 className="font-semibold text-slate-900">{d.title}</h3>
                            <Badge variant="secondary" className={sm.tone}>{sm.label}</Badge>
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700 border-slate-200 text-[10px]">{CATEGORY_LABELS[d.category]}</Badge>
                            {d.requires_board_approval && <Badge variant="secondary" className="bg-purple-50 text-purple-700 border-purple-200 text-[10px]">Requer Aprovação</Badge>}
                            {d.linked_module && d.linked_module !== 'general' && (
                              <Badge variant="secondary" className="bg-slate-50 text-slate-600 border-slate-200 text-[10px]">
                                {MODULE_LABELS[d.linked_module]}{d.linked_entity_id ? ` · ${d.linked_entity_id}` : ''}
                              </Badge>
                            )}
                          </div>
                          {d.description && <p className="text-sm text-slate-600 line-clamp-2 mb-1">{d.description}</p>}
                          <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                            <span>Criado por <strong>{d.created_by_name || d.created_by}</strong> em {d.created_at?.slice(0, 10)}</span>
                            {d.approved_at && (
                              <span className="text-emerald-600 flex items-center gap-1">
                                <ShieldCheck className="h-3 w-3" /> Aprovado por <strong>{d.approved_by_name || d.approved_by}</strong> em {d.approved_at.slice(0, 10)}
                              </span>
                            )}
                            {d.rejected_at && d.rejection_reason && (
                              <span className="text-red-600" title={d.rejection_reason}>Motivo rejeição: {d.rejection_reason.slice(0, 80)}…</span>
                            )}
                            {d.sha256_hash && <code className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded font-mono">SHA-256: {d.sha256_hash.slice(0, 12)}…</code>}
                            {d.size_bytes && <span>{Math.round(d.size_bytes / 1024)} KB</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0 flex-wrap">
                          {(d.content_base64 !== undefined || d.external_url || d.file_name) && (
                            <Button variant="ghost" size="sm" onClick={() => download(d)} data-testid={`doc-download-${d.doc_id}`}>
                              {d.external_url ? <ExternalLink className="h-3.5 w-3.5" /> : <Download className="h-3.5 w-3.5" />}
                            </Button>
                          )}
                          {canSubmit && (
                            <Button variant="outline" size="sm" onClick={() => submitForApproval(d)} data-testid={`doc-submit-${d.doc_id}`}>
                              <Send className="h-3.5 w-3.5 mr-1" /> Submeter
                            </Button>
                          )}
                          {canApprove && (
                            <>
                              <Button variant="outline" size="sm" onClick={() => approve(d)} className="text-emerald-700 hover:bg-emerald-50 border-emerald-200" data-testid={`doc-approve-${d.doc_id}`}>
                                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Aprovar
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => setRejectDialog(d)} className="text-red-700 hover:bg-red-50 border-red-200" data-testid={`doc-reject-${d.doc_id}`}>
                                <XCircle className="h-3.5 w-3.5 mr-1" /> Rejeitar
                              </Button>
                            </>
                          )}
                          {canEdit && isOwner && user?.role === 'admin' && (
                            <Button variant="ghost" size="sm" onClick={() => remove(d)}>
                              <Trash2 className="h-3.5 w-3.5 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            }
          </CardContent>
        </Card>
      </div>

      {/* Create dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Novo documento</DialogTitle>
            <DialogDescription>Adicione uma evidência, política ou documento. Marque &quot;Requer aprovação&quot; se precisar do Órgão de Gestão.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Título *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="doc-form-title" /></div>
            <div><Label>Descrição</Label><Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Categoria *</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="doc-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(CATEGORY_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Módulo</Label>
                <Select value={form.linked_module} onValueChange={v => setForm({ ...form, linked_module: v })}>
                  <SelectTrigger data-testid="doc-form-module"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(MODULE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>ID do recurso ligado</Label><Input value={form.linked_entity_id} onChange={e => setForm({ ...form, linked_entity_id: e.target.value })} placeholder="Ex: BCM-1234, DPIA-5678" /></div>
              <div><Label>Data do documento</Label><Input type="date" value={form.document_date} onChange={e => setForm({ ...form, document_date: e.target.value })} /></div>
            </div>
            <div><Label>Tags (separadas por vírgula)</Label><Input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="anual, NIS2, art-20" /></div>
            <div className="space-y-2">
              <Label>Ficheiro (opcional, ≤10 MB)</Label>
              <div className="flex items-center gap-2">
                <input ref={fileInputRef} type="file" onChange={handleFile} className="hidden" id="doc-file-input" data-testid="doc-file-input" />
                <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="h-3.5 w-3.5 mr-1.5" /> Escolher ficheiro
                </Button>
                {fileName && <span className="text-xs text-slate-500 truncate">{fileName} ({Math.round((fileB64?.length || 0) * 0.75 / 1024)} KB)</span>}
              </div>
            </div>
            <div><Label>OU URL externo (Google Drive, SharePoint, etc.)</Label><Input value={form.external_url} onChange={e => setForm({ ...form, external_url: e.target.value })} placeholder="https://drive.google.com/..." /></div>
            <label className="flex items-center gap-2 text-sm cursor-pointer p-3 rounded-lg border border-purple-200 bg-purple-50" data-testid="doc-requires-approval-label">
              <input type="checkbox" checked={form.requires_board_approval} onChange={e => setForm({ ...form, requires_board_approval: e.target.checked })} data-testid="doc-form-requires-approval" />
              <span><strong>Requer aprovação do Órgão de Gestão</strong> (NIS2 Art. 20) — após criar, terás de submeter e um Board Member terá de aprovar formalmente</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={saving} data-testid="doc-form-submit">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A guardar…</> : 'Criar rascunho'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectDialog} onOpenChange={(o) => { if (!o) { setRejectDialog(null); setRejectReason(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeitar documento</DialogTitle>
            <DialogDescription>Indique o motivo da rejeição para que o autor possa corrigir.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Motivo *</Label>
            <Textarea rows={4} value={rejectReason} onChange={e => setRejectReason(e.target.value)} data-testid="doc-reject-reason" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setRejectDialog(null); setRejectReason(''); }}>Cancelar</Button>
            <Button onClick={reject} className="bg-red-600 hover:bg-red-700 text-white" data-testid="doc-reject-submit">Rejeitar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
